import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { AutoDirectDebitPayment } from '@portal-workspace/grow-shared-library';
import { BankingService } from '../../../service/banking.service';
import { CustomContentLoaderComponent, PortalHotToastService, CustomPaginatorComponent } from '@portal-workspace/grow-ui-library';
import { SelectionModel } from '@angular/cdk/collections';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FlexModule } from '@angular/flex-layout/flex';
import { createAsyncStore, loadingFor } from '@ngneat/loadoff';
import { NgClass, AsyncPipe } from '@angular/common';
import moment from 'moment';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
    selector: 'direct-debit-list',
    templateUrl: './direct-debit-list.component.html',
    styleUrls: ['./direct-debit-list.component.scss'],
    standalone: true,
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    imports: [
        CommonModule,
        MatTableModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        MatButtonModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatMenuModule,
        MatCheckboxModule,
        MatTooltipModule,
        FormsModule,
        CustomContentLoaderComponent,
        CustomPaginatorComponent,
        FlexModule,
        NgClass,
        AsyncPipe,
    ],
    providers: [BankingService]
})
export class DirectDebitListComponent implements OnInit {
    displayedColumns: string[] = [
        'select',
        'id',
        'pismoAccountId',
        'pismoStatementId',
        'amount',
        'status',
        'attemptCount',
        'createdDate',
        'lastAttemptDate',
        'errorMessage',
        'actions'
    ];

    dataSource: AutoDirectDebitPayment[] = [];
    total = 0;
    limit = 20;
    offset = 0;
    expandedElementId: number | null = null;
    selection = new SelectionModel<AutoDirectDebitPayment>(true, []);
    moment = moment;
    loader = loadingFor('tableLoading');
    store = createAsyncStore();

    // Filters
    statusFilter = 'all';
    accountIdFilter = '';
    startDate: Date | null = null;
    endDate: Date | null = null;

    statusOptions = [
        { value: 'all', label: 'All' },
        { value: 'pending', label: 'Pending' },
        { value: 'processing', label: 'Processing' },
        { value: 'success', label: 'Success' },
        { value: 'failed', label: 'Failed' },
        { value: 'skipped', label: 'Skipped' }
    ];

    isLoading = false;
    hasFilters = false;

    constructor(
        private bankingService: BankingService,
        private toastService: PortalHotToastService
    ) { }

    ngOnInit(): void {
        this.loadData();
    }

    loadData(): void {
        this.isLoading = true;
        this.selection.clear();

        const opt = {
            page: {
                limit: this.limit,
                offset: this.offset,
            },
            filter: '',
            sorts: [{ prop: 'createdDate' as const, dir: 'DESC' as const }],
            statusFilter: this.statusFilter !== 'all' ? this.statusFilter : undefined,
            accountId: this.accountIdFilter ? Number(this.accountIdFilter) : undefined,
            startDate: this.startDate ? moment(this.startDate).format('YYYY-MM-DD') : null,
            endDate: this.endDate ? moment(this.endDate).format('YYYY-MM-DD') : null,
        };

        this.updateFiltersStatus();

        this.bankingService.getDirectDebitPaymentsFn(opt).subscribe({
            next: (response) => {
                this.dataSource = response.payload;
                this.total = response.total;
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error loading direct debit payments:', error);
                this.toastService.error('Error loading direct debit payments');
                this.isLoading = false;
            }
        });
    }

    onPagination(event: PageEvent): void {
        this.offset = event.pageIndex;
        this.limit = event.pageSize;
        this.loadData();
    }

    applyFilters(): void {
        this.offset = 0;
        this.loadData();
    }

    clearFilters(): void {
        this.statusFilter = 'all';
        this.accountIdFilter = '';
        this.startDate = null;
        this.endDate = null;
        this.offset = 0;
        this.loadData();
    }

    updateFiltersStatus(): void {
        const hasStatusFilter = this.statusFilter !== 'all';
        const hasAccountIdFilter = this.accountIdFilter.trim() !== '';
        const hasDateFilter = this.startDate !== null || this.endDate !== null;

        this.hasFilters = hasStatusFilter || hasAccountIdFilter || hasDateFilter;
    }

    getStatusClass(status: string): string {
        switch (status) {
            case 'success':
                return 'status-success';
            case 'failed':
                return 'status-failed';
            case 'pending':
                return 'status-pending';
            case 'processing':
                return 'status-processing';
            case 'skipped':
                return 'status-skipped';
            default:
                return '';
        }
    }

    formatDate(dateString: string | null): string {
        if (!dateString) return '-';
        return moment(dateString).format('DD/MM/YYYY HH:mm');
    }

    formatAmount(amount: number): string {
        return `$${amount.toFixed(2)}`;
    }

    // Selection methods
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.length;
        return numSelected === numRows;
    }

    toggleAllRows() {
        if (this.isAllSelected()) {
            this.selection.clear();
        } else {
            this.dataSource.forEach(row => this.selection.select(row));
        }
    }

    toggleRow(row: AutoDirectDebitPayment) {
        this.selection.toggle(row);
    }

    showRetryButton(payment: AutoDirectDebitPayment): boolean {
        return payment.status === 'failed';
    }

    retryPayment(payment: AutoDirectDebitPayment): void {
        this.retryPayments([payment.id]);
    }

    retrySelected(): void {
        const selectedIds = this.selection.selected.map(payment => payment.id);
        if (selectedIds.length === 0) {
            this.toastService.error('Please select at least one payment to retry');
            return;
        }

        this.retryPayments(selectedIds);
    }

    private retryPayments(paymentIds: number[]): void {
        this.bankingService.retryDirectDebitPaymentsFn(paymentIds).pipe(
            this.toastService.spinnerObservable(),
        ).subscribe({
            next: (response) => {
                if (response.status) {
                    this.toastService.success(response.message || 'Payments retried successfully');
                    this.loadData();
                } else {
                    this.toastService.error(response.message || 'Failed to retry payments');
                }
            },
            error: (error) => {
                console.error('Error retrying payments:', error);
                this.toastService.error('Error retrying payments');
            }
        });
    }
}

